<script setup lang="ts">
import { type Component, computed, nextTick, ref } from "vue";
import { DynamicScroller, DynamicScrollerItem } from "vue-virtual-scroller";

import Animated from "~/components/dumb/Animated.vue";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import LoadingSpinner from "~/components/dumb/LoadingSpinner.vue";
import Template from "~/components/dumb/Template.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { MAX_ITEMS_PER_SECTION, ROW_TYPE } from "~/constants/dropdown";
import { AlertIcon, PlusCircleIcon, RecommendWithAiIcon } from "~/icons";
import { Placement, UserRole } from "~/shared/enums";
import type { MultiselectDropdownMenuItem, MultiselectDropdownMenuSection } from "~/shared/types";
import { usePageStore, useUserStore } from "~/stores";
import { makeUuid } from "~/utils/common";

const props = defineProps<{
  filteredSections: MultiselectDropdownMenuSection[];
  items: MultiselectDropdownMenuItem[];
  search: string | undefined;
  placeholder: string | undefined;
  itemClasses: string[] | undefined;
  enableNewEntry: boolean | undefined;
  newEntryIsValid: boolean | undefined;
  newEntryError: string | undefined;
  newEntryAllowed: boolean;
  newEntryIcon: Component | undefined;
  newEntryIconArgs: object | undefined;
  makeNewEntryText: ((search: string) => string) | undefined;
  hasNewEntrySlot: boolean | undefined;
  infiniteList: boolean | undefined;
  loadingMore: boolean | undefined;
  showRecommendationButton: boolean | undefined;
  block: boolean | undefined;
  autoSize: boolean | undefined;
  widthPixels: number | undefined;
  colors: Record<string, string> | undefined;
  showAll: boolean | undefined;
}>();

const emit = defineEmits<{
  select: [item: MultiselectDropdownMenuItem];
  remove: [value: string | boolean | number | null];
  create: [];
  replace: [value: string | boolean | number | null];
  updateSearch: [value: string];
  recommend: [];
  keydown: [event: KeyboardEvent];
  backspace: [event: KeyboardEvent];
}>();

const pageStore = usePageStore();
const userStore = useUserStore();

const calcWidthPx = computed(() => props.widthPixels ?? 224);

const internalSearch = ref("");

const searchInput = ref<HTMLInputElement | null>(null);
const inputId = ref(`multiselect-search-${makeUuid()}`);
const menu = ref<{ $el: HTMLElement } | null>(null);

const isMobile = computed(() => pageStore.isMobile);
const selectedItems = computed(() => {
  const seen = new Set();
  return props.items.filter((item) => {
    if (!item.selected || seen.has(item.value)) {
      return false;
    }
    seen.add(item.value);
    return true;
  });
});
const allItemsSelected = computed(() => props.items.every((item) => item.selected));

const search = computed(() => props.search ?? internalSearch.value);

const expandedSections = ref<Set<string>>(new Set());

const toggleSection = (title: string) => {
  if (expandedSections.value.has(title)) {
    expandedSections.value.delete(title);
  } else {
    expandedSections.value.add(title);
    nextTick(() => {
      const section = props.filteredSections.find((e) => e.title === title);
      const firstNewItem = section?.items[MAX_ITEMS_PER_SECTION];
      if (firstNewItem) {
        const targetElement = menu.value?.$el?.querySelector(
          `[data-item-value="${firstNewItem.value}"][tabindex="0"]`
        ) as HTMLElement;
        targetElement?.focus();
      }
    });
  }
};

const rows = computed(() => {
  const res: {
    id: string;
    type: string;
    section: MultiselectDropdownMenuSection;
    item?: MultiselectDropdownMenuItem;
  }[] = [];

  const hasMultipleSections = props.filteredSections.filter((e) => e.items.length > 0).length > 1;

  props.filteredSections.forEach((section, index) => {
    if (section.items.length === 0) {
      return;
    }

    const isExpanded = expandedSections.value.has(section.title);
    const hasEnoughHiddenItems = hasMultipleSections && section.items.length > MAX_ITEMS_PER_SECTION + 1;
    const visibleItems =
      hasEnoughHiddenItems && !isExpanded && !props.showAll
        ? section.items.slice(0, MAX_ITEMS_PER_SECTION)
        : section.items;

    res.push({ id: `section-${section.title}-start`, type: ROW_TYPE.SECTION_START, section });
    visibleItems.forEach((item) => {
      const id = item.customKey || `item-${item.value}-${item.label}`;
      res.push({ id, type: ROW_TYPE.ITEM, item, section });
    });

    if (hasEnoughHiddenItems && !isExpanded && !props.showAll) {
      res.push({ id: `section-${index}-show-more`, type: ROW_TYPE.SHOW_MORE, section });
    }

    res.push({ id: `section-${section.title}-end`, type: ROW_TYPE.SECTION_END, section });
  });
  return res;
});

const onBackspace = (event: KeyboardEvent) => {
  if (search.value !== "") {
    return;
  }
  emit("backspace", event);
};

const getAllRows = () => {
  const focusableElements = menu.value?.$el?.querySelectorAll('[tabindex="0"]') || [];
  return Array.from(focusableElements).map((element) => ({
    element: element as HTMLElement,
    isFocused: document.activeElement === element,
  }));
};

const onSelectItem = (item: MultiselectDropdownMenuItem) => {
  emit("select", item);
};

const clearSearch = () => {
  internalSearch.value = "";
  emit("updateSearch", "");
};

defineExpose({
  $el: menu,
  clearExpandedSections: () => expandedSections.value.clear(),
  clearSearch,
  focusSearch: () => searchInput.value?.focus(),
  getAllRows,
});
</script>

<template>
  <DynamicScroller
    ref="menu"
    class="overflow-y-auto"
    :class="[
      block && !pageStore.isMobile && 'w-full min-w-[224px]',
      !pageStore.isMobile ? 'max-h-96 rounded-t border app-drag-none bg-lt border-md focus-ring-none' : 'size-full',
    ]"
    :items="rows"
    :min-item-size="1"
    key-field="id"
    :style="{
      '--background': colors?.borderVlt,
      '--highlight': colors?.borderMd,
      ...(autoSize
        ? {}
        : {
            width: `${calcWidthPx}px`,
            'max-width': `${calcWidthPx}px`,
          }),
    }"
    @keydown.stop="emit('keydown', $event)">
    <template #before>
      <Animated
        v-if="selectedItems.length && !isMobile"
        class="flex cursor-text flex-wrap items-center gap-1 px-3 pt-3"
        @click="searchInput?.focus()"
        @keydown.enter="searchInput?.focus()">
        <div v-for="item in selectedItems" :key="item.value?.toString()" class="cursor-default overflow-hidden">
          <component
            :is="item.component"
            v-bind="item.componentArgs"
            :label="item.label"
            :selected="item.selected"
            :disabled="item.disabled"
            is-chip
            @remove="emit('remove', item.value)" />
        </div>
      </Animated>

      <form v-if="!isMobile" class="relative mb-1 flex items-center" autocomplete="off" @submit.prevent>
        <label :for="inputId" class="sr-only">Search</label>
        <input
          :id="inputId"
          ref="searchInput"
          v-model="internalSearch"
          type="text"
          class="block w-full border-x-0 border-b border-t-0 bg-transparent text-sm text-hvy border-md focus-ring-none placeholder:text-vlt focus-visible:ring-gray-200 focus-visible:border-md dark:focus-visible:ring-zinc-700"
          :class="items.length === selectedItems.length && 'border-none'"
          :placeholder="placeholder"
          @input="emit('updateSearch', internalSearch)"
          @keydown.backspace="onBackspace"
          @keydown.delete="onBackspace" />
        <div class="absolute right-0 flex items-center pr-4" />
      </form>
    </template>

    <template #default="{ item: { id, section, item, type }, index, active }">
      <!-- Section start and end -->
      <DynamicScrollerItem
        v-if="type === ROW_TYPE.SECTION_START || type === ROW_TYPE.SECTION_END"
        :item="{ id }"
        :active="active"
        :size-dependencies="[]"
        class="w-full truncate text-vlt border-md"
        :class="{
          'border-b pb-1': type === ROW_TYPE.SECTION_END && index < rows.length - 1,
          'pt-1': type === ROW_TYPE.SECTION_START,
        }">
        <span
          v-if="section.showTitle && type === ROW_TYPE.SECTION_START"
          class="select-none px-3 text-xs/3 font-semibold uppercase">
          {{ section.title }}
        </span>
      </DynamicScrollerItem>

      <!-- Show more -->
      <DynamicScrollerItem
        v-else-if="type === ROW_TYPE.SHOW_MORE && !showAll"
        :item="{ id }"
        :active="active"
        tabindex="0"
        class="w-full cursor-pointer focus-ring-none hover:bg-md focus:bg-md"
        @keydown.enter.stop="toggleSection(section.title)">
        <button
          type="button"
          class="flex w-full items-center justify-center px-3 py-1.5 text-xs font-medium text-vlt"
          tabindex="-1"
          @click.prevent.stop="toggleSection(section.title)"
          @mousedown.prevent>
          Show more
        </button>
      </DynamicScrollerItem>

      <!-- Item -->
      <DynamicScrollerItem
        v-else-if="item"
        :item="{ id }"
        :active="active"
        :size-dependencies="[]"
        tabindex="0"
        :disabled="item.disabled"
        :data-item-value="item.value"
        class="group/menu-item flex focus-ring-none"
        :class="[
          ...(itemClasses ?? []),
          item.disabled ? 'cursor-default' : 'cursor-pointer hover:bg-md focus-visible:bg-md',
          isMobile ? 'py-2.5 pl-5 pr-4' : 'py-1 pl-3 pr-2',
        ]"
        @click.prevent="onSelectItem(item)"
        @keydown.enter.prevent.stop="onSelectItem(item)">
        <component
          :is="item.disabled && item.disabledReason ? Tooltip : Template"
          :placement="Placement.LEFT"
          :text="item.disabledReason"
          block
          :show-delay="200">
          <div v-if="item.disabled" class="absolute inset-0" />
          <component
            :is="item.component"
            v-bind="item.componentArgs"
            :label="item.label"
            :selected="item.selected"
            :disabled="item.disabled"
            @select="onSelectItem"
            @replace="emit('replace', item.value)" />
        </component>
      </DynamicScrollerItem>
    </template>

    <template #after>
      <LoadingSpinner v-if="infiniteList && loadingMore" :class="isMobile && 'py-3'" />
      <Tooltip
        v-if="search.length > 0 && enableNewEntry && newEntryAllowed"
        :disabled="newEntryIsValid"
        :text="newEntryError"
        block
        :placement="Placement.TOP">
        <div
          tabindex="0"
          class="flex w-full select-none items-center gap-2 text-sm"
          :class="[
            newEntryIsValid
              ? 'cursor-pointer text-md focus-ring-none hover:bg-md focus-visible:bg-md'
              : 'cursor-default text-vlt',
            isMobile ? 'py-2.5 pl-5 pr-4' : 'py-1 pl-3 pr-2',
          ]"
          @click.prevent="emit('create')"
          @keydown.enter.prevent.stop="emit('create')">
          <component
            v-bind="newEntryIconArgs"
            :is="hasNewEntrySlot ? null : (newEntryIcon ?? PlusCircleIcon)"
            class="text-vlt icon-sm"
            :class="!newEntryIsValid && 'opacity-50'" />
          <span v-if="!hasNewEntrySlot" class="hyphens-auto break-words">
            {{ makeNewEntryText?.(search) ?? `Create ${search}` }}
          </span>
          <slot v-else name="new-entry-slot" />
        </div>
      </Tooltip>

      <div
        v-else-if="filteredSections.flatMap((section) => section.items).length === 0"
        tabindex="0"
        class="flex cursor-default select-none items-center gap-2 text-sm opacity-50 text-vlt"
        :class="isMobile ? 'py-2.5 pl-5 pr-4' : 'py-1 pl-3 pr-2'">
        <component
          :is="
            search.length > 0 || (allItemsSelected && !enableNewEntry) || !newEntryAllowed ? AlertIcon : PlusCircleIcon
          "
          class="text-vlt icon-md" />
        <span>
          {{
            (allItemsSelected && !enableNewEntry) || !newEntryAllowed
              ? `No${items.length > 0 ? " more" : ""} options`
              : search.length > 0
                ? "No results, search again"
                : "Type to add an option"
          }}
        </span>
      </div>
      <div class="mt-1" />

      <div
        v-if="isMobile && showRecommendationButton && pageStore.isOnline && userStore.isRoleGte(UserRole.MEMBER)"
        class="w-full border-t px-2 py-1 border-lt">
        <DropdownMenuItemContent
          class="cursor-pointer select-none text-sm text-md"
          :icon="RecommendWithAiIcon"
          :icon-args="{
            class: 'icon-sm text-recommendation-base dark:text-recommendation-base',
          }"
          title="Fill out with AI"
          @click="emit('recommend')" />
      </div>
    </template>
  </DynamicScroller>

  <div
    v-if="!isMobile && showRecommendationButton && pageStore.isOnline && userStore.isRoleGte(UserRole.MEMBER)"
    class="w-full rounded-b border-x border-b py-1 app-drag-none bg-lt border-md">
    <DropdownMenuItemContent
      class="cursor-pointer select-none text-sm bg-lt text-md hover:bg-md"
      :icon="RecommendWithAiIcon"
      :icon-args="{
        class: 'icon-sm text-recommendation-base dark:text-recommendation-base',
      }"
      title="Fill out with AI"
      @click="emit('recommend')" />
  </div>
</template>
